import { toAddressPayload } from '@/utils/address';
import { resolveHomeLocation } from '@/utils/homeLocation';
import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEmployeeStore } from '../../store/useEmployeeStore.ts';
import { useWorkScheduleModelStore } from '../../store/useWorkScheduleModelStore.ts';
import { useTimeClockGroupStore } from '../../store/useTimeClockGroupStore.ts';
import { useToastStore } from '../../store/useToastStore.ts';
import { Card } from '../../components/Shared/Card.tsx';
import { Loading } from '../../components/Shared/Loading.tsx';
import { Button } from '../../components/Shared/Button.tsx';
import { Badge } from '../../components/Shared/Badge.tsx';
import { EmptyState } from '../../components/Shared/EmptyState.tsx';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Shield,
  Edit,
  Key,
  UserCheck,
  UserX,
  Trash2,
  Clock,
  CreditCard,
  Home,
  Hash,
  FileDown,
} from 'lucide-react';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import { useReportDownload } from '../../hooks/useReportDownload';
import { EmployeeDialog, type EmployeeFormData } from './components/EmployeeDialog.tsx';
import { timeClockGroupForUpdate } from './components/employeeTimeClockGroup.ts';
import { PasswordResetDialog } from './components/PasswordResetDialog.tsx';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog.tsx';
import { MonthDownloadPicker } from '../../components/Shared/MonthDownloadPicker.tsx';
import { formatDate, isoDate } from '../../utils/dateUtils';

export function EmployeeDetail() {
  const { t } = useTranslation(['employees', 'common', 'reports']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentEmployee,
    loading,
    error,
    fetchEmployeeById,
    updateEmployee,
    assignWorkScheduleModel,
    deleteEmployee,
    activateEmployee,
    deactivateEmployee,
    resetPassword,
    clearError,
  } = useEmployeeStore();
  const {
    models: workScheduleModels,
    currentModel: currentWorkScheduleModel,
    fetchModels: fetchWorkScheduleModels,
    fetchModelById: fetchWorkScheduleModelById,
  } = useWorkScheduleModelStore();
  const timeClockGroups = useTimeClockGroupStore((s) => s.groups);
  const fetchTimeClockGroups = useTimeClockGroupStore((s) => s.fetchGroups);
  const { addToast } = useToastStore();
  const { downloading: reportDownloading, download: downloadReportFn } = useReportDownload();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchEmployeeById(id);
    }
    // Load work schedule models for dropdown
    fetchWorkScheduleModels(false);
    void fetchTimeClockGroups();
  }, [id, fetchEmployeeById, fetchWorkScheduleModels, fetchTimeClockGroups]);

  // Load current work schedule model details when employee has one assigned
  useEffect(() => {
    if (currentEmployee?.workScheduleModelId) {
      fetchWorkScheduleModelById(currentEmployee.workScheduleModelId);
    }
  }, [currentEmployee?.workScheduleModelId, fetchWorkScheduleModelById]);

  const handleBack = () => {
    navigate('/employees');
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleSaveEmployee = async (data: EmployeeFormData) => {
    if (!id) return;
    
    try {
      // Home location = start point in the planning assistant; saving never fails because of it.
      const home = await resolveHomeLocation(data.address, currentEmployee);
      await updateEmployee(id, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        role: data.role,
        // why: personalNumber is required by UpdateEmployeeRequest. Always send it; an incomplete
        // value is rejected by the API with a per-field error. The address is optional.
        personalNumber: data.personalNumber || '',
        birthDate: data.birthDate || null,
        hireDate: data.hireDate || null,
        socialSecurityNumber: data.socialSecurityNumber || null,
        iban: data.iban || null,
        bic: data.bic || null,
        address: toAddressPayload(data.address),
        ...(home.coords ?? {}),
        ...(home.clear ? { clearHomeLocation: true } : {}),
        allowsHomeOffice: data.allowsHomeOffice,
        ...timeClockGroupForUpdate(data),
      });
      addToast('Mitarbeiter erfolgreich aktualisiert', 'success');
      if (home.failed) addToast(t('employees:messages.geocodeFailed'), 'warning');
      await fetchEmployeeById(id);
    } catch (error: unknown) {
      // Check if it's a validation error (400) with field-specific errors
      const axiosError = error as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } };
      if (axiosError?.response?.status === 400 && axiosError?.response?.data?.errors) {
        // Re-throw to let the dialog handle field-specific errors
        throw error;
      } else {
        addToast(t('employees:toasts.updateError'), 'error');
        throw new Error('Update failed');
      }
    }
  };

  const handleWorkScheduleModelChange = async (workScheduleModelId: string) => {
    if (!id || !currentEmployee) return;

    try {
      // Dedicated endpoint: only touches the model (and regenerates the next 90 days of
      // planning entries) — no need to re-send the whole profile.
      await assignWorkScheduleModel(id, workScheduleModelId || null);
      addToast(t('employees:toasts.workScheduleAssigned'), 'success');
      await fetchEmployeeById(id);
    } catch {
      addToast(t('employees:toasts.workScheduleError'), 'error');
    }
  };

  const handlePasswordReset = async (newPassword: string) => {
    if (!id) return;
    
    try {
      await resetPassword(id, newPassword);
      addToast(t('employees:toasts.passwordResetSuccess'), 'success');
    } catch {
      addToast(t('employees:toasts.passwordResetError'), 'error');
      throw new Error('Password reset failed');
    }
  };

  const handleActivate = async () => {
    if (!id) return;
    
    try {
      await activateEmployee(id);
      await fetchEmployeeById(id);
      addToast(t('employees:toasts.activateSuccess'), 'success');
    } catch {
      addToast(t('employees:toasts.activateError'), 'error');
    }
  };

  const handleDeactivate = async () => {
    if (!id) return;
    
    try {
      await deactivateEmployee(id);
      await fetchEmployeeById(id);
      addToast(t('employees:toasts.deactivateSuccess'), 'success');
    } catch {
      addToast(t('employees:toasts.deactivateError'), 'error');
    }
  };

  const handleDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id || !currentEmployee) return;
    
    try {
      await deleteEmployee(id);
      addToast(t('employees:toasts.deleteSuccess'), 'success');
      navigate('/employees');
    } catch {
      addToast(t('employees:toasts.deleteError'), 'error');
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  const getStatusBadge = (status: string | null | undefined) => {
    switch (status) {
      case 'Active':
        return <Badge variant="success">{t('employees:statusBadges.active')}</Badge>;
      case 'Inactive':
        return <Badge variant="warning">{t('employees:statusBadges.inactive')}</Badge>;
      case 'Terminated':
        return <Badge variant="danger">{t('employees:statusBadges.terminated')}</Badge>;
      default:
        return <Badge>{t('employees:statusBadges.unknown')}</Badge>;
    }
  };

  const getRoleBadge = (role: string | null | undefined) => {
    return role === 'Verwaltung' 
      ? <Badge variant="info">{t('employees:roles.administration')}</Badge>
      : <Badge>{t('employees:roles.employee')}</Badge>;
  };

  if (loading && !currentEmployee) {
    return <Loading />;
  }

  if (error && !currentEmployee) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleBack}
          leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
        >
          {t('employees:detail.backToOverview')}
        </Button>
        <Card>
          <EmptyState
            variant="error"
            title={t('employees:messages.loadError')}
            description={error}
            onRetry={() => {
              clearError();
              if (id) fetchEmployeeById(id);
            }}
          />
        </Card>
      </div>
    );
  }

  if (!currentEmployee) {
    return null;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={handleBack}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('employees:detail.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
                {currentEmployee.fullName}
              </h1>
              {getStatusBadge(currentEmployee.status)}
            </div>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('employees:detail.employeeNumber')}: <span className="font-mono-num text-slate-700">{currentEmployee.personalNumber ?? '—'}</span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <Button
              variant="secondary"
              onClick={handleEdit}
              leftIcon={<Edit className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('employees:detail.edit')}
            </Button>
            {currentEmployee.status !== 'Terminated' && (
              <Button
                variant="secondary"
                onClick={() => setPasswordResetOpen(true)}
                leftIcon={<Key className="w-4 h-4" strokeWidth={2} />}
                className="w-full sm:w-auto"
              >
                <span className="sm:hidden">{t('employees:detail.password')}</span>
                <span className="hidden sm:inline">{t('employees:detail.resetPassword')}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card title={t('employees:detail.sectionPersonalInfo')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4">
                <Field label={t('employees:fields.firstName')}>
                  {currentEmployee.firstName || '—'}
                </Field>
                <Field label={t('employees:fields.lastName')}>
                  {currentEmployee.lastName || '—'}
                </Field>
                <Field label={t('employees:fields.email')} icon={<Mail className="w-3.5 h-3.5" strokeWidth={2} />}>
                  {currentEmployee.email || '—'}
                </Field>
              </div>
              <div className="space-y-4">
                <Field label={t('employees:fields.phone')} icon={<Phone className="w-3.5 h-3.5" strokeWidth={2} />}>
                  <span className="tabular-nums">{currentEmployee.phone || '—'}</span>
                </Field>
                <Field label={t('employees:fields.birthDate')} icon={<Calendar className="w-3.5 h-3.5" strokeWidth={2} />}>
                  <span className="tabular-nums">
                    {currentEmployee.birthDate
                      ? new Date(currentEmployee.birthDate).toLocaleDateString('de-DE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : '—'}
                  </span>
                </Field>
              </div>
            </div>
          </Card>

          {/* Employment Information */}
          <Card title={t('employees:detail.sectionEmploymentInfo')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Field label={t('employees:detail.hiredOn')}>
                <span className="tabular-nums">
                  {currentEmployee.hireDate
                    ? new Date(currentEmployee.hireDate).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : '—'}
                </span>
              </Field>
              {currentEmployee.terminationDate && (
                <Field label={t('employees:detail.terminatedOn')}>
                  <span className="tabular-nums">
                    {new Date(currentEmployee.terminationDate).toLocaleDateString('de-DE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </Field>
              )}
              <Field label={t('employees:fields.allowsHomeOffice')}>
                {currentEmployee.allowsHomeOffice
                  ? t('employees:fields.allowsHomeOfficeYes')
                  : t('employees:fields.allowsHomeOfficeNo')}
              </Field>
              <Field label={t('employees:fields.timeClockGroup')}>
                {(() => {
                  if (!currentEmployee.timeClockGroupId) {
                    return t('employees:fields.timeClockGroupDefault');
                  }
                  const group = timeClockGroups.find(
                    (g) => g.timeClockGroupId === currentEmployee.timeClockGroupId,
                  );
                  if (!group) return t('employees:fields.timeClockGroupUnknown');
                  return group.isActive
                    ? group.name
                    : t('employees:fields.timeClockGroupInactive', { name: group.name });
                })()}
              </Field>
            </div>
          </Card>

          {/* Additional Details: SV-Nummer, Bank, Address */}
          <Card title={t('employees:detail.sectionAdditionalDetails')}>
            <div className="space-y-5">
              <Field
                label={t('employees:fields.socialSecurityNumber')}
                icon={<Hash className="w-3.5 h-3.5" strokeWidth={2} />}
              >
                <span className="font-mono-num">{currentEmployee.socialSecurityNumber || '—'}</span>
              </Field>

              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                  <CreditCard className="w-3.5 h-3.5" strokeWidth={2} />
                  {t('employees:detail.bankDetails')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] text-slate-500 mb-0.5">{t('employees:fields.iban')}</div>
                    <div className="text-[13px] text-slate-900 font-mono-num">
                      {currentEmployee.iban || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 mb-0.5">{t('employees:fields.bic')}</div>
                    <div className="text-[13px] text-slate-900 font-mono-num">
                      {currentEmployee.bic || '—'}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                  <Home className="w-3.5 h-3.5" strokeWidth={2} />
                  {t('employees:detail.homeAddress')}
                </div>
                {currentEmployee.address ? (
                  <div className="text-[13px] text-slate-700 space-y-0.5">
                    <p>
                      {currentEmployee.address.street} {currentEmployee.address.streetNumber}
                    </p>
                    <p className="tabular-nums">
                      {currentEmployee.address.postalCode} {currentEmployee.address.city}
                    </p>
                    {currentEmployee.address.country && (
                      <p className="text-[12px] text-slate-500">
                        {currentEmployee.address.country}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-[13px] text-slate-500">—</p>
                )}
              </div>
            </div>
          </Card>

          {/* Work Schedule Model (Arbeitszeitmodell) */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" strokeWidth={2} />
                {t('employees:detail.sectionWorkSchedule')}
              </span>
            }
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="workScheduleModel" className="text-[11px] font-medium uppercase tracking-wider text-slate-500 block mb-1.5">
                  {t('employees:detail.assignedModel')}
                </label>
                <select
                  id="workScheduleModel"
                  value={currentEmployee.workScheduleModelId || ''}
                  onChange={(e) => handleWorkScheduleModelChange(e.target.value)}
                  className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50 disabled:bg-slate-50 disabled:cursor-not-allowed"
                  disabled={currentEmployee.status === 'Terminated'}
                >
                  <option value="">{t('employees:detail.noModelAssigned')}</option>
                  {workScheduleModels.map((model) => (
                    <option key={model.workScheduleModelId} value={model.workScheduleModelId}>
                      {model.name} ({model.weeklyHours}h/Woche)
                    </option>
                  ))}
                </select>
              </div>

              {currentWorkScheduleModel && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <h3 className="text-[13px] font-semibold text-slate-900 mb-2">
                    {t('employees:detail.details')}
                  </h3>
                  <div className="space-y-1.5 text-[12px]">
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('employees:detail.weeklyHours')}:</span>
                      <span className="text-slate-900 font-medium tabular-nums">
                        {currentWorkScheduleModel.weeklyHours}h
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('employees:detail.workDays')}:</span>
                      <span className="text-slate-900 font-medium tabular-nums">
                        {currentWorkScheduleModel.workDays?.filter(d => d.isWorkingDay).length || 0}
                      </span>
                    </div>
                    {(() => {
                      // A planned version of the model (FAFD-25) — the details above are today's.
                      const latest = currentWorkScheduleModel.versions?.[currentWorkScheduleModel.versions.length - 1];
                      return latest?.validFrom && latest.validFrom > isoDate(new Date()) ? (
                        <p className="text-[11px] text-amber-700">
                          {t('workScheduleModels:table.plannedChangeFrom', { date: formatDate(latest.validFrom) })}
                        </p>
                      ) : null;
                    })()}
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <p className="text-[11px] text-slate-500 mb-1.5">{t('employees:detail.weekSchedule')}:</p>
                      <div className="space-y-0.5">
                        {currentWorkScheduleModel.workDays?.map((day) => {
                          const dayNames: Record<number, string> = {
                            0: t('employees:dayNames.sun'), 1: t('employees:dayNames.mon'), 2: t('employees:dayNames.tue'), 3: t('employees:dayNames.wed'),
                            4: t('employees:dayNames.thu'), 5: t('employees:dayNames.fri'), 6: t('employees:dayNames.sat')
                          };
                          return (
                            <div key={day.dayOfWeek} className="flex justify-between text-[12px]">
                              <span className="text-slate-500">
                                {dayNames[day.dayOfWeek || 0]}:
                              </span>
                              <span className="text-slate-700 tabular-nums">
                                {day.isWorkingDay
                                  ? `${day.startTime?.substring(0, 5)}–${day.endTime?.substring(0, 5)}`
                                  : t('employees:detail.dayOff')}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Status & Role Card */}
          <Card title={t('employees:detail.sectionStatusRole')}>
            <div className="space-y-4">
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                  {t('employees:detail.status')}
                </div>
                <div className="mt-1">
                  {getStatusBadge(currentEmployee.status)}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" strokeWidth={2} />
                  {t('employees:detail.role')}
                </div>
                <div className="mt-1">
                  {getRoleBadge(currentEmployee.role)}
                </div>
              </div>
              {currentEmployee.lastLoginAt && (
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                    {t('employees:detail.lastLogin')}
                  </div>
                  <p className="text-[13px] text-slate-700 mt-0.5 tabular-nums">
                    {new Date(currentEmployee.lastLoginAt).toLocaleString('de-DE')}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Actions Card */}
          <Card title={t('employees:detail.actions')}>
            <div className="space-y-2">
              {currentEmployee.status === 'Active' ? (
                <Button
                  variant="secondary"
                  className="w-full justify-start"
                  onClick={handleDeactivate}
                  leftIcon={<UserX className="w-4 h-4 text-amber-600" strokeWidth={2} />}
                >
                  {t('employees:detail.deactivate')}
                </Button>
              ) : currentEmployee.status === 'Inactive' ? (
                <Button
                  variant="secondary"
                  className="w-full justify-start"
                  onClick={handleActivate}
                  leftIcon={<UserCheck className="w-4 h-4 text-emerald-600" strokeWidth={2} />}
                >
                  {t('employees:detail.activate')}
                </Button>
              ) : null}
              {currentEmployee.status !== 'Terminated' && (
                <Button
                  variant="secondary"
                  className="w-full justify-start !text-red-600 hover:!bg-red-50 hover:!border-red-200"
                  onClick={handleDelete}
                  leftIcon={<Trash2 className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('employees:detail.deleteEmployee')}
                </Button>
              )}

              <div className="border-t border-slate-100 pt-3 mt-3">
                <h4 className="text-[13px] font-semibold text-slate-900 mb-2">
                  {t('reports:title')}
                </h4>
                <div className="space-y-2">
                  <Button
                    variant="secondary"
                    className="w-full justify-start"
                    disabled={reportDownloading}
                    onClick={() => {
                      const now = new Date();
                      downloadReportFn('weekly', { year: getISOWeekYear(now), week: getISOWeek(now) }, id);
                    }}
                    leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
                  >
                    {t('reports:types.weekly')}
                  </Button>
                  <Button
                    variant="secondary"
                    className="w-full justify-start"
                    disabled={reportDownloading}
                    onClick={() => {
                      const now = new Date();
                      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                      downloadReportFn('daily', { date: dateStr }, id);
                    }}
                    leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
                  >
                    {t('reports:types.daily')}
                  </Button>
                  <Button
                    variant="secondary"
                    className="w-full justify-start"
                    disabled={reportDownloading}
                    onClick={() => {
                      const now = new Date();
                      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                      downloadReportFn('daily', { date: dateStr, includeChecklists: true }, id);
                    }}
                    leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
                  >
                    {t('reports:types.dailyWithChecklists')}
                  </Button>
                  <MonthDownloadPicker
                    label={t('reports:types.monthly')}
                    monthLabel={t('reports:labels.month')}
                    downloading={reportDownloading}
                    onDownload={(year, month) => downloadReportFn('monthly', { year, month }, id)}
                    icon={<FileDown className="w-4 h-4" strokeWidth={2} />}
                    min={currentEmployee.hireDate ? currentEmployee.hireDate.slice(0, 7) : undefined}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <EmployeeDialog
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveEmployee}
        employee={currentEmployee}
        mode="edit"
      />

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        isOpen={passwordResetOpen}
        onClose={() => setPasswordResetOpen(false)}
        onReset={handlePasswordReset}
        employeeName={currentEmployee.fullName || ''}
        employeeEmail={currentEmployee.email || undefined}
        employeePhone={currentEmployee.phone || undefined}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('employees:deleteConfirm.title')}
        message={t('employees:deleteConfirm.message', { name: currentEmployee?.fullName })}
        confirmText={t('employees:deleteConfirm.confirm')}
        cancelText={t('employees:deleteConfirm.cancel')}
        variant="danger"
      />
    </div>
  );
}

interface FieldProps {
  label: string;
  icon?: ReactNode;
  children: ReactNode;
}

function Field({ label, icon, children }: FieldProps) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className="text-[13px] text-slate-900 font-medium mt-0.5">{children}</div>
    </div>
  );
}
